// Корневой роутинг. Структура:
//   /login, /policy                 — публичные, AuthLayout (без шапки)
//   /change-password                — authed + mustChangePassword=true, AuthLayout
//   /, /tenants, /users, ...         — authed + пароль уже сменён, AppShell
//   *                                — 404
//
// ProtectedRoute сам разбирается со всеми редиректами по статусу авторизации.

import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import { ProtectedRoute } from './auth/ProtectedRoute.jsx';
import { RoleGuard } from './auth/RoleGuard.jsx';
import { AppShell } from './layouts/AppShell.jsx';
import { AuthLayout } from './layouts/AuthLayout.jsx';
import { ErrorBoundary } from './components/ErrorBoundary.jsx';

// Auth/onboarding страницы маленькие, и они на критическом пути — оставляем eager.
import LoginPage from './pages/LoginPage.jsx';
import ChangePasswordPage from './pages/ChangePasswordPage.jsx';
import AcceptPdnPage from './pages/AcceptPdnPage.jsx';
import HomePage from './pages/HomePage.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';
import PolicyPage from './pages/PolicyPage.jsx';

// Тяжёлые страницы — code-split. Initial bundle снижается, переходы плавные
// (HomePage-редирект отрабатывает быстрее, чем юзер успевает заметить Suspense).
const TenantsListPage = lazy(() => import('./pages/tenants/ListPage.jsx'));
const TenantDetailPage = lazy(() => import('./pages/tenants/DetailPage.jsx'));
const UsersListPage = lazy(() => import('./pages/users/ListPage.jsx'));
const UserDetailPage = lazy(() => import('./pages/users/DetailPage.jsx'));
const PromptsEditorPage = lazy(() => import('./pages/prompts/EditorPage.jsx'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage.jsx'));
const TrainerPage = lazy(() => import('./pages/trainer/TrainerPage.jsx'));
const DialogsListPage = lazy(() => import('./pages/dialogs/ListPage.jsx'));
const DialogDetailPage = lazy(() => import('./pages/dialogs/DetailPage.jsx'));
const AnalyticsPage = lazy(() => import('./pages/analytics/Page.jsx'));

function PageFallback() {
    return (
        <Center py="xl">
            <Loader />
        </Center>
    );
}

export default function App() {
    return (
        <Routes>
            {/* Публичные */}
            <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/policy" element={<PolicyPage />} />
            </Route>

            {/* Принудительная смена пароля — authed но mustChangePassword=true */}
            <Route element={<ProtectedRoute mode="must-change-password" />}>
                <Route element={<AuthLayout />}>
                    <Route path="/change-password" element={<ChangePasswordPage />} />
                </Route>
            </Route>

            {/* Согласие с обработкой персональных данных (152-ФЗ) — после смены пароля */}
            <Route element={<ProtectedRoute mode="accept-pdn" />}>
                <Route element={<AuthLayout />}>
                    <Route path="/accept-pdn" element={<AcceptPdnPage />} />
                </Route>
            </Route>

            {/* Основной shell — authed + пароль готов + ПДн принято */}
            <Route element={<ProtectedRoute mode="ready" />}>
                <Route element={<AppShell />}>
                    <Route path="/" element={<HomePage />} />
                    <Route
                        path="/tenants"
                        element={
                            <RoleGuard roles={['creator', 'curator']}>
                                <TenantsListPage />
                            </RoleGuard>
                        }
                    />
                    <Route
                        path="/tenants/:id"
                        element={
                            <RoleGuard roles={['creator', 'curator']}>
                                <TenantDetailPage />
                            </RoleGuard>
                        }
                    />
                    <Route path="/users" element={<UsersListPage />} />
                    <Route path="/users/:id" element={<UserDetailPage />} />
                    <Route
                        path="/prompts"
                        element={
                            <RoleGuard roles={['creator']}>
                                <ErrorBoundary>
                                    <PromptsEditorPage />
                                </ErrorBoundary>
                            </RoleGuard>
                        }
                    />
                    <Route
                        path="/settings"
                        element={
                            <RoleGuard roles={['creator']}>
                                <ErrorBoundary>
                                    <SettingsPage />
                                </ErrorBoundary>
                            </RoleGuard>
                        }
                    />
                    <Route
                        path="/analytics"
                        element={
                            <RoleGuard roles={['creator', 'admin', 'rop']}>
                                <AnalyticsPage />
                            </RoleGuard>
                        }
                    />
                    <Route path="/dialogs" element={<DialogsListPage />} />
                    <Route path="/dialogs/:id" element={<DialogDetailPage />} />
                    <Route path="/trainer" element={<TrainerPage />} />
                    <Route
                        path="/my-trainings"
                        element={
                            <RoleGuard roles={['creator', 'admin', 'rop']}>
                                <DialogsListPage scopeMyTrainings />
                            </RoleGuard>
                        }
                    />
                </Route>
            </Route>

            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
}
